import { defineStore } from 'pinia'
import { keyBy, pickBy, mapValues } from 'lodash-es'
import { appName, appVersion } from 'src/consts.js'
import { usePersistedFilters } from 'src/components/filters/filterUtils.js'

// We can put anything here that we want persisted across app reloads. Global/site-wide preferences only!
const { filters, resetFilters } = usePersistedFilters('app-preferences', {
    app_version: appVersion, // If this differs from current app version, prefs will be reset
    dark_mode: null, // auto-detect theme by default
})

export const useAppStore = defineStore('app', {
    state: () => ({
        // App lookups from server indexed by lookup category
        lookups: {},

        preferences: filters,

        isLoading: true,
        quickLinksMenuOpen: false,
        isFillingOutForm: false, // Whether user is currently filling out a form (see FormWrapper)

        // Page metadata - you should call setPageInfo on every new page
        page: {
            title: '',
            section: '',
            subtitle: '',
            icon: null, // Must be an image
        },

        // Additional runtime service configuration - loaded from server upon succesful auth.
        // See config/services.php
        services: {
            mapbox: {
                token: null,
                layers: [],
            },

            projectLocator: {
                url: null,
                token: null,
            },
        },

        // Any registered interval timers for the current page
        intervalTimers: new Set(),

    }),

    getters: {
        userStatusById: state => keyBy(state.lookups.user_status, 'id'),
        planStatusById: state => keyBy(state.lookups.plan_status, 'id'),
        mprStatusById: state => keyBy(state.lookups.mpr_status, 'id'),
        otherKeyRolesById: state => keyBy(state.lookups.other_key_role, 'id'),
        languagesByCode: state => keyBy(state.lookups.app_language, 'meta1'),

        businessGroupTypesWithoutCountry: state => {
            if (!state.lookups.business_group_types) return []

            let types = state.lookups.business_group_types.slice()
            types.shift() // Remove top country level it is not needed for filtering on
            return types
        },

        // Indexes all lookups by their category name and then by their ID
        lookupsById: state => {
            let indexed = { ...state.lookups }
            for (const category in state.lookups) {
                indexed[category] = keyBy(state.lookups[category], 'id')
            }
            return indexed
        },

        lookupsByRegion: state => {
            let grouped = {}

            const lookups = pickBy(
                mapValues(state.lookups, groupedLookups => Object.values(groupedLookups).filter(mixedLookup => mixedLookup && Object.hasOwn(mixedLookup, 'filtered_region_ids'))),
                lookups => lookups.length > 0,
            )

            for (const categoryName in lookups) {
                for (const lookup of state.lookups[categoryName]) {
                    for (const region of state.lookups.data_source_regions) {
                        let regionId = region.id
                        if (!Object.hasOwn(grouped, regionId)) grouped[regionId] = {}
                        if (!Object.hasOwn(grouped[regionId], categoryName)) grouped[regionId][categoryName] = []
                        if (!lookup.filtered_region_ids || lookup.filtered_region_ids.includes(regionId)) {
                            grouped[regionId][categoryName].push(lookup)
                        }
                    }
                }
            }

            return grouped
        },

        // Provides a easy way to quickly retrieve a conversion based on source and target currency.
        conversionRateMap: state => {
            let rates = new Map()
            for (const conversionRate of state.lookups.conversion_rates) {
                rates.set(`${conversionRate.from_currency_id}-${conversionRate.to_currency_id}`, conversionRate.rate)
            }
            return rates
        },

        accessibleDataSourceRegions: state => state.lookups.data_source_regions.filter(region => region.has_access),
    },

    actions: {
        resetPreferences() {
            localStorage.clear()
            resetFilters()
        },

        setLoading(isLoading) {
            this.isLoading = isLoading
        },

        setIsFillingOutForm(isFillingOutForm) {
            this.isFillingOutForm = isFillingOutForm
        },

        setPageInfo(pageConfig) {
            this.page = pageConfig

            document.title = pageConfig.title
                ? `${pageConfig.title} • ${appName}`
                : appName
        },

        // Load the fixed lookup data into the store to be made available to all pages
        setLookups(lookups) {
            this.lookups = lookups
        },

        setServices(mapbox, projectLocator) {
            this.services.mapbox = mapbox
            this.services.projectLocator = projectLocator
        },

        /**
         * Wrapper around window.setInterval that allows this store to keep track of the timer,
         * and auto-clear it upon page change
         */
        setInterval(fn, milliseconds) {
            let timer = setInterval(fn, milliseconds)
            this.intervalTimers.add(timer)
            return timer
        },

        /**
         * Clear any setInterval timers that had been set for the current page
         * Can optionally pass a specific timer to clear
         */
        clearInterval(timer = null) {
            if (timer) {
                this.clearInterval(timer)
                this.intervalTimers.delete(timer)
            } else if (this.intervalTimers.size) {
                for (const timer of this.intervalTimers) clearInterval(timer)
                this.intervalTimers.clear()
            }

        },
    },
})
